import React, { useRef, useState, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import { type UIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { Theme as ButtonTheme } from '@atlaskit/button';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import AddIcon from '@atlaskit/icon/core/migration/add';
import Portal from '@atlaskit/portal';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { getTheme } from '@atlassian/jira-common-theme/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { User } from '@atlassian/jira-issue-shared-types/src/common/types/user-type.tsx';
import type { AccountId, IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import messages from './messages.tsx';
import UserPicker from './user-picker/index.tsx';

export type AddWatchersProps = {
	issueKey: IssueKey;
	canManageWatchers: boolean;
	watchers: User[];
	onAddWatcher: (
		arg1: User,
		arg2: (accountId: AccountId | null) => void,
		arg3: () => void,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
	onShowAddWatchersAnalytics: (arg1: UIAnalyticsEvent) => void;
};

/**
 * The PopUp is rendered in a `modal` z-index layer
 * (to avoid a bug for Issue View in a modal, where it was hidden behind)
 * So for the select menu portal, we need to use same index + add on
 * top (otherwise it ends up being under the PopUp too)
 */
export const POPUP_SELECT_ZINDEX = layers.modal + 10;

// export class AddWatchers extends Component<Props, State> {
export const AddWatchers = ({
	issueKey,
	canManageWatchers,
	watchers,
	onAddWatcher,
	onShowAddWatchersAnalytics,
}: AddWatchersProps) => {
	const [showUserPicker, setShowUserPicker] = useState<boolean>();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { formatMessage } = useIntl();
	/**
	 * Because the PopUp component renders the select list
	 * inside it, it forces scrollbars within it,
	 * we will render the select in a portal above the PopUp
	 */
	const menuPortalTargetRef = useRef<HTMLDivElement>(null);

	const onClick = (clickEvent: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		setShowUserPicker(true);
		onShowAddWatchersAnalytics(analyticsEvent);
	};
	const onClickVisualRefresh = (_: SyntheticEvent) => {
		setShowUserPicker(true);
		onShowAddWatchersAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
		);
	};

	const buttonComponent = isVisualRefreshEnabled() ? (
		<DropdownItem
			testId="issue-view-watchers.watchers.dropdown.add-watchers.add-watcher"
			elemBefore={<AddIcon label="" spacing="spacious" LEGACY_size="medium" color="currentColor" />}
			onClick={onClickVisualRefresh}
			interactionName="auto-generated.jira-issue-view-watchers.watchers.view.dropdown.add-watchers.button"
		>
			{formatMessage(messages.link)}
		</DropdownItem>
	) : (
		<ButtonTheme.Provider value={addWatchersButtonTheme}>
			<Button
				onClick={onClick}
				shouldFitContainer
				appearance="subtle"
				iconBefore={<AddIcon label="" LEGACY_size="small" color="currentColor" />}
				interactionName="auto-generated.jira-issue-view-watchers.watchers.view.dropdown.add-watchers.button"
			>
				{formatMessage(messages.link)}
			</Button>
		</ButtonTheme.Provider>
	);

	if (canManageWatchers) {
		return (
			<Container>
				{showUserPicker && (
					<UserPicker
						issueKey={issueKey}
						watchers={watchers}
						onAddWatcher={onAddWatcher}
						menuPortalTarget={menuPortalTargetRef}
					/>
				)}
				{!showUserPicker && buttonComponent}
				{/* The select menu portal, if not */}
				<Portal zIndex={POPUP_SELECT_ZINDEX}>
					<div ref={menuPortalTargetRef} />
				</Portal>
			</Container>
		);
	}
	return null;
};

export default AddWatchers;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Container = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderTop: `1px solid ${token('color.border', colors.N30)}`,
});

export const addWatchersButtonTheme = getTheme({
	buttonStyles: {
		subtle: {
			color: token('color.text.brand'),
			'text-align': 'left',
			'border-radius': '0px',
		},
	},
});
